import React from 'react';
import { Box, Group, Text, Badge, Collapse, UnstyledButton } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './CollapsibleSection.css';

interface CollapsibleSectionProps {
  title: string;
  count?: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
  alwaysShowContent?: boolean; // For sections that should never collapse (used in mobile optimization)
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  isOpen,
  onToggle,
  children,
  icon,
  alwaysShowContent = false
}) => {
  return (
    <Box className="collapsible-section">
      <UnstyledButton
        onClick={onToggle}
        className={`collapsible-header ${isOpen ? 'open' : 'closed'}`}
        aria-expanded={isOpen}
        aria-controls={`section-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <Group justify="space-between" wrap="nowrap" w="100%">
          <Group gap="xs" wrap="nowrap">
            {isOpen ? (
              <ChevronDown size={16} className="chevron-icon" />
            ) : (
              <ChevronRight size={16} className="chevron-icon" />
            )}
            {icon && <Box className="section-icon">{icon}</Box>}
            <Text size="sm" fw={500} c="var(--app-text-primary)">
              {title}
            </Text>
          </Group>
          {count !== undefined && count > 0 && (
            <Badge 
              size="sm" 
              variant="light" 
              color="blue"
              styles={{
                root: {
                  textTransform: 'none',
                  fontWeight: 600
                }
              }}
            >
              {count}
            </Badge>
          )}
        </Group>
      </UnstyledButton>
      
      {alwaysShowContent ? (
        <Box className="collapsible-content always-visible" pt="xs">
          {children}
        </Box>
      ) : (
        <Collapse 
          in={isOpen}
          transitionDuration={200}
          transitionTimingFunction="ease-in-out"
        >
          <Box className="collapsible-content" pt="xs">
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
